import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Message, Table } from 'semantic-ui-react';
import { fetchLogs } from '../api/index';

/**
 * Server log viewer.
 *
 * Rewritten. The previous version drove the message tracker — a live feed of
 * every relayed device message, with start/stop controls, a split pane and a
 * JSON inspector, backed by the `messagetrackers` collection. That collection
 * was 50 MB of the production database and nothing read it; the relay it
 * observed is gone along with the qcloud/qsrv split.
 *
 * What remains is the capped `niq:log` list the server writes to, which is
 * enough to answer "what happened just now" without a database.
 *
 * This drops react-split-pane and react-json-view from the app.
 */

const Wrapper = styled.div`
  padding: 2em;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.5em;
  margin-bottom: 1em;
`;

const Time = styled.td`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

interface LogEntry {
  time?: number | string;
  level?: number | string;
  msg?: string;
  message?: string;
  [key: string]: unknown;
}

const LEVELS: Record<number, string> = {
  10: 'trace',
  20: 'debug',
  30: 'info',
  40: 'warn',
  50: 'error',
  60: 'fatal'
};

function levelName(level: LogEntry['level']): string {
  if (typeof level === 'number') return LEVELS[level] ?? String(level);
  return level ?? '';
}

function formatTime(time: LogEntry['time']): string {
  if (!time) return '';
  const date = new Date(typeof time === 'number' ? time : Date.parse(String(time)));
  return Number.isNaN(date.getTime()) ? String(time) : date.toLocaleString();
}

export function AdminLogsPage() {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const response: any = await fetchLogs({ limit: 200 });
      setEntries(response?.data ?? []);
    } catch {
      setError('Nepodařilo se načíst log.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Wrapper>
      <Toolbar>
        <Button icon="refresh" content="Obnovit" onClick={() => void load()} loading={busy} />
      </Toolbar>

      {error ? <Message negative content={error} /> : null}

      <Table compact size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Čas</Table.HeaderCell>
            <Table.HeaderCell>Úroveň</Table.HeaderCell>
            <Table.HeaderCell>Zpráva</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {entries.map((entry, index) => (
            <Table.Row key={index}>
              <Time>{formatTime(entry.time)}</Time>
              <Table.Cell>{levelName(entry.level)}</Table.Cell>
              <Table.Cell>{entry.msg ?? entry.message ?? ''}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {!busy && entries.length === 0 && !error ? (
        <Message info content="Log je prázdný." />
      ) : null}
    </Wrapper>
  );
}
